import React, { useState } from "react";
import { 
  Modal, 
  Box, 
  IconButton, 
  TextField, 
  Button, 
  Paper,
  Typography,
  Tooltip
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAtom } from "jotai";
import { themeAtom } from "../../../../-Atoms";

const URLModalV2 = ({ open, handleClose, url }) => {
  const [theme] = useAtom(themeAtom);
  const [copySuccess, setCopySuccess] = useState(false);

  const copyURL = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const openURL = () => {
    window.open(url, "_blank");
  };

  const urlIsImage = (str) => {
    if (!str) return false;
    const lowerStr = str.toLowerCase();
    return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".heic", ".bmp", ".svg"].some(
      ext => lowerStr.includes(ext)
    );
  }


  // Dropbox URLs need to be modified to be displayed directly.
  const processedUrl = (url || "").includes("dropbox.com")
    ? (url || "").replace("dl=0", "raw=1")
    : url || "";

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50,
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      y: 30,
      transition: {
        duration: 0.2,
      }
    }
  };


  return (
    <AnimatePresence>
      {open && (
        <Modal 
          open={open} 
          onClose={handleClose}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
          }}
          closeAfterTransition
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "98vw",
              height: "98vh",
              maxWidth: "none",
              maxHeight: "none",
              outline: "none",
            }}
          >
              <Paper
                elevation={24}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 3,
                  overflow: "hidden",
                  background: theme === "dark" 
                    ? "linear-gradient(135deg, rgba(28, 28, 28, 0.95) 0%, rgba(35, 35, 35, 0.95) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.95) 100%)",
                  backdropFilter: "blur(20px)",
                  border: theme === "dark" 
                    ? "1px solid rgba(255, 255, 255, 0.1)" 
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: theme === "dark" 
                      ? "1px solid rgba(255, 255, 255, 0.1)" 
                      : "1px solid rgba(0, 0, 0, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    background: theme === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.02)",
                  }}
                >
                  <TextField
                    value={processedUrl}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: theme === "dark" 
                          ? "rgba(255, 255, 255, 0.05)" 
                          : "rgba(0, 0, 0, 0.02)",
                      },
                    }}
                  />
                  
                  <Tooltip title={copySuccess ? "Copied!" : "Copy URL"}>
                    <IconButton 
                      onClick={copyURL}
                      sx={{
                        color: copySuccess 
                          ? "#4caf50" 
                          : theme === "dark" ? "#90caf9" : "#1976d2",
                        "&:hover": {
                          backgroundColor: theme === "dark" 
                            ? "rgba(144, 202, 249, 0.1)" 
                            : "rgba(25, 118, 210, 0.1)",
                        },
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Open in new tab">
                    <IconButton 
                      onClick={openURL}
                      sx={{
                        color: theme === "dark" ? "#90caf9" : "#1976d2",
                        "&:hover": {
                          backgroundColor: theme === "dark" 
                            ? "rgba(144, 202, 249, 0.1)" 
                            : "rgba(25, 118, 210, 0.1)",
                        },
                      }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Close">
                    <IconButton 
                      onClick={handleClose}
                      sx={{
                        color: theme === "dark" ? "#ffffff" : "#000000",
                        "&:hover": {
                          backgroundColor: theme === "dark" 
                            ? "rgba(255, 255, 255, 0.1)" 
                            : "rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Content */}
                <Box
                  sx={{
                    flex: 1,
                    p: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: theme === "dark" 
                      ? "rgba(0, 0, 0, 0.2)" 
                      : "rgba(255, 255, 255, 0.5)",
                  }}
                >
                  {urlIsImage(processedUrl) ? (
                    <motion.img
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      src={processedUrl}
                      alt="Attachment"
                      style={{
                        maxWidth: "calc(100% - 8px)",
                        maxHeight: "calc(100vh - 120px)",
                        objectFit: "contain",
                        borderRadius: 8,
                        boxShadow: theme === "dark" 
                          ? "0 8px 32px rgba(0, 0, 0, 0.5)"
                          : "0 8px 32px rgba(0, 0, 0, 0.15)",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = "flex";
                        }
                      }}
                    />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                      style={{ width: "100%", height: "100%" }}
                    >
                      <iframe
                        title="Document Viewer"
                        src={processedUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          borderRadius: 8,
                          backgroundColor: theme === "dark" ? "#1a1a1a" : "#ffffff",
                        }}
                        onError={(e) => {
                          console.error("Iframe failed to load:", e);
                        }}
                      />
                    </motion.div>
                  )}

                  {/* Fallback error display */}
                  <Box
                    sx={{
                      display: "none",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 4,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
                      Unable to preview this file
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={openURL}
                      startIcon={<OpenInNewIcon />}
                      sx={{
                        borderRadius: 2,
                        background: theme === "dark"
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                      }}
                    >
                      Open in New Tab
                    </Button>
                  </Box>
                </Box>
              </Paper>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default URLModalV2;